// @flow

import React, {Children} from 'react';
import Icon, {Icons, Sizes} from '@pb/kit-component-icon';
import {Tooltip} from '@pb/kit-component-popups';
import {Dropdown} from '@pb/kit-component-dropdown-v2';
import {Group, Item} from '@pb/kit-component-group';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import styles from './info.component.module.styl';

import type {Node, StatelessFunctionalComponent as FC} from 'react';
import type {IconSize} from '@pb/kit-component-icon';
import type {DropdownProps} from '@pb/kit-component-dropdown-v2';


const classNames = withCssModule(styles);
const classes = hashedClasses(styles);
const noop = () => undefined;

type PositionType = 'top' | 'right' | 'bottom' | 'left';

type Props = {|
    /**
     * Max allowed width of hint
     */
    maxWidth?: number,
    /**
     * Type of Info
     * */
    type?: {| tooltip?: string, popover?: Node |},
    /**
     * Inline style
     */
    inline?: boolean,
    /**
     * Handler for mouse enter event
     */
    onMouseEnter?: () => void,
    /**
     * Click handler
     */
    onClick?: (e: SyntheticEvent<>) => void,
    /**
     * Reverse order for icon & text
     */
    reverseOrder?: boolean,
    /**
     * Custom ClassName
     */
    mix?: string,
    /**
     * Custom ClassName for pointer
     */
    mixPointer?: string,
    /**
     * Child elements
     */
    children?: Node,
    /**
     * Info position
     */
    position?: PositionType,
    /**
     * Tooltip type
     */
    tooltipType?: 'dark' | 'light',
    /**
     * Show SmallPopover pointer
     */
    showPopoverPointer?: boolean,
    /**
     * Ellipsis content
     */
    ellipsisContent?: boolean,
    /**
     * If should increase padding
     */
    increasedPadding?: boolean,
    /**
     * Is shown info
     */
    shown?: boolean,
    /**
     * onShow callback
     */
    onShow?: () => void,
    /**
     * Specified icon size
     */
    iconSize?: IconSize,
    /**
     * Dropdown props
     */
    dropdownProps?: $Shape<DropdownProps>,
|};

/**
 * @deprecated It will be removed in 6 months 12-19-2026. Use `InfoIconButton` from @pb/kit-component-icon-button together with `Tooltip` (@pb/kit-component-popups) or `Dropdown` (@pb/kit-component-dropdown-v2) instead. See InfoIconButton documentation: https://storybook.dev.pitchbookdata.com/?path=/docs/buttons-info-icon--info-icon-with-tooltip
 */
export const Info: FC<Props> = (
    {
        inline = true,
        type,
        onClick,
        onMouseEnter = noop,
        maxWidth,
        children,
        reverseOrder = false,
        mix = '',
        mixPointer = '',
        position,
        tooltipType,
        showPopoverPointer = true,
        ellipsisContent = true,
        shown,
        onShow,
        increasedPadding = false,
        iconSize = Sizes.Size_12,
        dropdownProps,
    }: Props): React$Node => {

    const onShowHandler = (isShown: boolean): mixed => {
        if (isShown && onShow) {
            onShow();
        }
    };

    const infoClasses = classNames(
        'info',
        {
            info_pointer: !type || onClick,
            [mixPointer]: !type || onClick,
        },
    );

    const infoIcon = <span className={classes.info__icon}><Icon size={iconSize} type={Icons.INFO}/></span>;
    const infoIconAnchor = ({anchorRef, getAnchorProps}) => (
        <span className={classes.info__icon} ref={anchorRef} {...getAnchorProps({'aria-expanded': undefined})}>
            <Icon size={iconSize} type={Icons.INFO}/>
        </span>
    );

    const info = (
        <span
            onClick={onClick}
            className={infoClasses}
            onMouseEnter={onMouseEnter}>
                {type && type.tooltip ? (
                    <Tooltip
                        anchor={infoIconAnchor}
                        color={tooltipType}
                        placement={position}>
                        {type.tooltip}
                    </Tooltip>
                ) : null}
            {type && type.popover ? (
                <Dropdown
                    anchor={infoIconAnchor}
                    arrowEnabled={showPopoverPointer}
                    className={increasedPadding ? 'pd-l' : undefined}
                    hoverable={true}
                    maxWidth={maxWidth}
                    shown={shown}
                    onToggle={onShowHandler}
                    {...(dropdownProps ?? ({}: $Shape<DropdownProps>))}>
                    {type.popover}
                </Dropdown>
            ) : null}
            {!type && infoIcon}
            </span>
    );
    const childrenWrapperClasses = classNames(
        'info-container__children group__i_hid',
        {ellipsis: ellipsisContent}
    );
    const childrenWrapper = <Item className={childrenWrapperClasses}>{children}</Item>;
    const infoWrapper = <Item>{info}</Item>;

    if (Children.count(children)) {
        return (
            <Group
                className={classNames('info-container', mix)}
                display={inline ? 'inline-flex' : 'flex'}
                alignItems="center"
                gap="xs">
                {reverseOrder ? infoWrapper : childrenWrapper}
                {reverseOrder ? childrenWrapper : infoWrapper}
            </Group>
        );
    }

    return info;
};
